import React, { useEffect, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import * as UIcons from '@iconscout/react-unicons'
import useSWR, { mutate } from 'swr'
import dayjs from 'dayjs'
import Btn from 'components/Btn'
import { apiRequest } from 'store/Misc/apiRequest'
import { destructureErrorMessage } from 'Helper'
import PageHeader from '../shared/PageHeader'
import ConfirmDialog from '../shared/ConfirmDialog'
import UserFilterBar from './components/UserFilterBar'
import UserTable from './components/UserTable'
import UserForm from './components/UserForm'
import AssignRoleModal from './components/AssignRoleModal'
import UserDetail from './components/UserDetail'

export default withTranslation()(function UserManagement({ t }) {
  const { data, isValidating } = useSWR('user-mgmt/users')
  const { data: branchesRes } = useSWR('merchants/branches')

  const [q, setQ] = useState('')
  const [branch, setBranch] = useState('')
  const [status, setStatus] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [lastUpdated, setLastUpdated] = useState(dayjs())

  const [viewingUserId, setViewingUserId] = useState(null)
  const [editingUser, setEditingUser] = useState(null)
  const [assigningRoleUser, setAssigningRoleUser] = useState(null)
  const [toggleTarget, setToggleTarget] = useState(null)
  const [resetTarget, setResetTarget] = useState(null)
  const [busy, setBusy] = useState(false)

  const users = data?.data?.data ?? []
  const branches = branchesRes?.data ?? []
  const branchNameById = branches.reduce((acc, item) => ({ ...acc, [item.id]: item.name }), {})
  const viewingUser = users.find((user) => user.id === viewingUserId)

  useEffect(() => {
    if (!isValidating) setLastUpdated(dayjs())
  }, [isValidating])

  const isActive = (user) => (user.status ?? 'active') !== 'inactive' && user.status !== 'suspended'

  const refresh = () => {
    mutate('user-mgmt/users')
    mutate('merchants/branches')
  }

  const confirmToggleStatus = async () => {
    const willDisable = isActive(toggleTarget)
    setBusy(true)
    try {
      await apiRequest({
        method: 'put',
        url: `user-mgmt/users/${toggleTarget.id}`,
        payload: { status: willDisable ? 'inactive' : 'active' }
      })
      mutate('user-mgmt/users')
      toast.success(t(`settings.user_management.success_${willDisable ? 'disable' : 'enable'}`))
      setToggleTarget(null)
    } catch ({ message }) {
      destructureErrorMessage(message).map((msg) => toast.error(msg, { autoClose: 15000 }))
    } finally {
      setBusy(false)
    }
  }

  const confirmResetPassword = async () => {
    setBusy(true)
    try {
      await apiRequest({ method: 'post', url: `user-mgmt/users/${resetTarget.id}/reset-password` })
      toast.success(t('settings.user_management.success_reset_password'))
      setResetTarget(null)
    } catch ({ message }) {
      destructureErrorMessage(message).map((msg) => toast.error(msg, { autoClose: 15000 }))
    } finally {
      setBusy(false)
    }
  }

  const assignRole = async (roleId) => {
    setBusy(true)
    try {
      await apiRequest({
        method: 'put',
        url: `user-mgmt/users/${assigningRoleUser.id}`,
        payload: { role_id: roleId }
      })
      mutate('user-mgmt/users')
      toast.success(t('settings.user_management.success_assign_role'))
      setAssigningRoleUser(null)
    } catch ({ message }) {
      destructureErrorMessage(message).map((msg) => toast.error(msg, { autoClose: 15000 }))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {
        viewingUser ? (
          <UserDetail
            user={viewingUser}
            branchName={branchNameById[viewingUser.branch_id] ?? '—'}
            onEdit={() => setEditingUser(viewingUser)}
            onAssignRole={() => setAssigningRoleUser(viewingUser)}
            onResetPassword={() => setResetTarget(viewingUser)}
            onToggleStatus={() => setToggleTarget(viewingUser)}
          />
        ) : (
          <section className="w-full">
            <PageHeader
              crumbs={[
                { name: t('settings.hub.title'), url: '/settings' },
                { name: t('settings.hub.team.title'), url: '/settings/team-access' },
                { name: t('settings.user_management.page_title') }
              ]}
              title={t('settings.user_management.page_title')}
              right={
                <div className="flex items-center gap-2">
                  <span className="text-sm text-secondary whitespace-nowrap">
                    {t('settings.user_management.last_update', { date: lastUpdated.format('D MMM YY') })}
                  </span>

                  <button
                    type="button"
                    onClick={refresh}
                    className="w-9 h-9 rounded-full border border-border-lines flex items-center justify-center hover:bg-background shrink-0"
                  >
                    <UIcons.UilRedo className="text-secondary" size={16} />
                  </button>

                  <Btn
                    content={() => <span className="flex items-center gap-1.5"><UIcons.UilPlus size={16} /> {t('settings.user_management.add')}</span>}
                    className="text-white bg-primary rounded-full h-9 px-4 font-medium"
                    click={() => setShowAdd(true)}
                  />
                </div>
              }
            />

            <UserFilterBar
              q={q}
              onChangeQ={setQ}
              branches={branches}
              branch={branch}
              onChangeBranch={setBranch}
              status={status}
              onChangeStatus={setStatus}
            />

            <UserTable
              q={q}
              branch={branch}
              status={status}
              onViewDetail={(user) => setViewingUserId(user.id)}
              onEdit={setEditingUser}
              onAssignRole={setAssigningRoleUser}
              onResetPassword={setResetTarget}
              onToggleStatus={setToggleTarget}
            />
          </section>
        )
      }

      {
        showAdd && (
          <UserForm
            handleClick={() => setShowAdd(false)}
            onSaved={() => mutate('user-mgmt/users')}
          />
        )
      }

      {
        editingUser && (
          <UserForm
            user={editingUser}
            handleClick={() => setEditingUser(null)}
            onSaved={() => mutate('user-mgmt/users')}
          />
        )
      }

      {
        assigningRoleUser && (
          <AssignRoleModal
            user={assigningRoleUser}
            loading={busy}
            onCancel={() => setAssigningRoleUser(null)}
            onConfirm={assignRole}
          />
        )
      }

      {
        toggleTarget && (
          <ConfirmDialog
            title={t(isActive(toggleTarget) ? 'settings.user_management.deactivate' : 'settings.user_management.enable')}
            description={t(isActive(toggleTarget) ? 'settings.user_management.disable_confirm' : 'settings.user_management.enable_confirm')}
            confirmText={t(isActive(toggleTarget) ? 'settings.user_management.deactivate' : 'settings.user_management.enable')}
            danger={isActive(toggleTarget)}
            loading={busy}
            onConfirm={confirmToggleStatus}
            onCancel={() => setToggleTarget(null)}
          />
        )
      }

      {
        resetTarget && (
          <ConfirmDialog
            title={t('settings.user_management.reset_password')}
            description={t('settings.user_management.reset_password_confirm')}
            confirmText={t('settings.user_management.reset_password')}
            danger={false}
            loading={busy}
            onConfirm={confirmResetPassword}
            onCancel={() => setResetTarget(null)}
          />
        )
      }
    </>
  )
})
